import React from 'react';

/**
 * HeroNetworkCanvas
 * ------------------------------------------------------------------
 * Homepage background layer.
 *
 * The first screen must show a real engineering / lab image behind the
 * SALAR message. This component intentionally avoids mascot/character art
 * and avoids a dark canvas-only effect. It uses existing public website
 * image assets and a lighter overlay so the background remains visible while
 * the headline stays readable.
 */
export default function HeroNetworkCanvas() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="home-real-lab-hero-bg">
      <style>{`
        @keyframes salarHeroSlowPan {
          0% { transform: scale(1.03) translate3d(0, 0, 0); }
          50% { transform: scale(1.07) translate3d(-1.2%, -0.6%, 0); }
          100% { transform: scale(1.03) translate3d(0, 0, 0); }
        }
        @keyframes salarHeroLabGlow {
          0%, 100% { opacity: 0.18; transform: translate3d(0, 0, 0); }
          50% { opacity: 0.32; transform: translate3d(-2%, 1%, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .salar-hero-real-photo,
          .salar-hero-lab-glow { animation: none !important; }
        }
      `}</style>

      {/* Real photograph: engineering / electronics workbench context. */}
      <img
        src="/img/about-salar-circuit-board.jpg"
        alt=""
        className="salar-hero-real-photo absolute inset-0 h-full w-full object-cover"
        style={{
          animation: 'salarHeroSlowPan 22s ease-in-out infinite',
          objectPosition: 'center 36%',
          filter: 'saturate(1.12) contrast(1.08) brightness(0.92)',
        }}
        loading="eager"
        decoding="async"
      />

      {/* Secondary right-side technical texture so the photo does not look flat. */}
      <img
        src="/img/pexels_fiber_cables.jpg"
        alt=""
        className="absolute inset-y-0 right-0 hidden h-full w-[48%] object-cover opacity-45 mix-blend-screen md:block"
        style={{
          objectPosition: 'center center',
          filter: 'saturate(1.15) contrast(1.12) brightness(0.86)',
          maskImage: 'linear-gradient(90deg, transparent 0%, black 35%, black 100%)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 35%, black 100%)',
        }}
        loading="eager"
        decoding="async"
      />

      {/* Readability layers: lighter than before so the image is actually visible. */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.78)_0%,rgba(4,12,28,0.62)_48%,rgba(4,12,28,0.36)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.18)_0%,rgba(2,6,23,0.04)_42%,rgba(2,6,23,0.58)_100%)]" />

      {/* Subtle SALAR-style assurance glow and grid, kept behind the copy. */}
      <div className="salar-hero-lab-glow absolute right-[8%] top-[18%] h-[34rem] w-[34rem] rounded-full bg-cyan-300/20 blur-3xl" style={{ animation: 'salarHeroLabGlow 12s ease-in-out infinite' }} />
      <div className="absolute left-0 top-0 h-full w-full opacity-[0.07] bg-[linear-gradient(rgba(103,232,249,0.22)_1px,transparent_1px),linear-gradient(90deg,rgba(103,232,249,0.16)_1px,transparent_1px)] bg-[size:88px_88px]" />
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#060A14] to-transparent" />
    </div>
  );
}
